import type { PaywallCurrency } from "@circle-react/types";

import { fetchRequest } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";

// PAY-404 -- must match `InternalApi::ConversionTrackingController::FUNNEL_EVENT_NAMES`.
export const ConversionTrackingEvent = {
  BEGIN_CHECKOUT: "begin_checkout",
  ADD_PAYMENT_INFO: "add_payment_info",
} as const;

export type ConversionTrackingEventName =
  (typeof ConversionTrackingEvent)[keyof typeof ConversionTrackingEvent];

// PAY-404 -- loosely follows GA4/Meta ecommerce item conventions (no vendor
// sender exists yet -- Phase 2 -- so exact field naming isn't load-bearing).
export interface ConversionTrackingItem {
  item_id: number;
  price: number;
  quantity: number;
}

export interface TrackConversionEventParams {
  checkoutSessionId?: number;
  // PAY-404 -- read live at fire time (never cached). Mirrors the money
  // event's own `value`/`currency` fields (`ConfirmService`, Slice 1.3).
  currency?: string;
  // Browser-only precision; never forwarded in the server POST.
  currencySmallestUnit?: PaywallCurrency["smallest_unit"];
  eventName: ConversionTrackingEventName;
  // PAY-404 -- fresh per fire, never reused. `add_payment_info` can
  // legitimately fire more than once per funnel run (e.g. switching back to
  // a payment type that already fired), and the backend's short-TTL
  // idempotency check is keyed on `funnel_uuid` alone -- folding this nonce
  // into the server-side dedup key stops that legitimate re-fire from being
  // collapsed as a false duplicate, while a true same-fire retry (same
  // nonce) still collapses correctly.
  fireNonce?: string;
  funnelUuid: string;
  // PAY-404 -- only for `BEGIN_CHECKOUT`; `ADD_PAYMENT_INFO` has no "items"
  // concept in GA4/Meta ecommerce semantics.
  items?: ConversionTrackingItem[];
  // Stripe's payment-method-type string (`event.value.type`). Passed through
  // as-is with no enum, since Stripe's own set of types can grow independent
  // of this codebase.
  paymentType?: string;
  paywallId?: number;
  paywallPriceId?: number;
  // PAY-404 -- only for `ADD_PAYMENT_INFO`, and only when the price's
  // scenario actually starts with a trial (`trial_then_value` /
  // `trial_then_upfront_then_value`).
  trialDays?: number;
  value?: number;
}

// No `community_id` in the payload -- the controller resolves the real
// community from the request host (`current_community`), never a
// FE-supplied param.
const track = ({
  eventName,
  funnelUuid,
  checkoutSessionId,
  paywallId,
  paywallPriceId,
  paymentType,
  fireNonce,
  value,
  currency,
  items,
  trialDays,
}: TrackConversionEventParams) =>
  fetchRequest<Response>(internalApi.conversionTracking.create(), {
    method: "POST",
    body: JSON.stringify({
      event_name: eventName,
      funnel_uuid: funnelUuid,
      checkout_session_id: checkoutSessionId,
      paywall_id: paywallId,
      paywall_price_id: paywallPriceId,
      payment_type: paymentType,
      fire_nonce: fireNonce,
      value,
      currency,
      items,
      trial_days: trialDays,
    }),
  });

export const conversionTrackingApi = {
  track,
};
